import {
  generateLeaveHolidaysResultSchema,
  leaveAdminSetupSchema,
  leaveDepartmentCapacityListSchema,
  leaveDepartmentCapacityItemSchema,
  leaveHolidayListSchema,
  leaveHolidaySchema,
  leavePolicySchema,
  leaveAbsenceTypeListSchema,
  leaveAbsenceTypeSchema,
  leaveBalanceAdjustmentSchema,
  leaveCompanyClosurePreviewSchema,
  leaveCompanyClosureResultSchema,
  leaveExportCountSchema,
  leaveExportOptionsSchema,
  leavePayrollExportSchema,
  setPersonnelNumberSchema,
  leaveOvertimeEntrySchema,
  leaveWorkPatternSchema,
  okResponseSchema,
  type CreateCompanyHolidayRequest,
  type CreateLeaveBalanceAdjustmentRequest,
  type CreateLeaveOvertimeRequest,
  type LeaveCompanyClosureWrite,
  type LeaveExportFilter,
  type LeaveExportFormatCode,
  type LeavePayrollExport,
  type SetPersonnelNumber,
  type UpdateLeavePolicyRequest,
  type CreateLeaveAbsenceTypeRequest,
  type UpdateLeaveAbsenceTypeRequest,
  type UpsertLeaveDepartmentCapacityRequest,
  type UpsertLeaveWorkPatternRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, apiFetch } from '@/lib/api';

export const leavePolicyKey = ['leave', 'policy'] as const;
export const leaveAdminSetupKey = ['leave', 'setup'] as const;
export const leaveHolidayListKey = (year: number) => ['leave', 'holidays', year] as const;
export const leaveAbsenceTypeCatalogKey = ['leave', 'absence-types', 'catalog'] as const;
export const leaveCapacityKey = ['leave', 'capacity'] as const;

export function useLeavePolicyAdmin() {
  return useQuery({
    queryKey: leavePolicyKey,
    queryFn: () => apiFetch('/leave/policy', leavePolicySchema),
  });
}

export function useUpdateLeavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLeavePolicyRequest) =>
      apiFetch('/leave/policy', leavePolicySchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leavePolicyKey });
    },
  });
}

export function useLeaveHolidays(year: number) {
  return useQuery({
    queryKey: leaveHolidayListKey(year),
    queryFn: () => apiFetch(`/leave/holidays?year=${year}`, leaveHolidayListSchema),
  });
}

export function useLeaveAdminSetup() {
  return useQuery({
    queryKey: leaveAdminSetupKey,
    queryFn: () => apiFetch('/leave/setup', leaveAdminSetupSchema),
  });
}

export function useGenerateLeaveHolidays() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      apiFetch(`/leave/holidays/generate?year=${year}`, generateLeaveHolidaysResultSchema, {
        method: 'POST',
      }),
    onSuccess: async (_data, year) => {
      await queryClient.invalidateQueries({ queryKey: leaveHolidayListKey(year) });
      await queryClient.invalidateQueries({ queryKey: leaveAdminSetupKey });
    },
  });
}

export function useCreateCompanyHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCompanyHolidayRequest) =>
      apiFetch('/leave/holidays', leaveHolidaySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (holiday) => {
      await queryClient.invalidateQueries({ queryKey: leaveHolidayListKey(holiday.year) });
    },
  });
}

export function useDeleteLeaveHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, year }: { id: string; year: number }) =>
      apiFetch(`/leave/holidays/${id}`, okResponseSchema, {
        method: 'DELETE',
      }).then(() => year),
    onSuccess: async (year) => {
      await queryClient.invalidateQueries({ queryKey: leaveHolidayListKey(year) });
    },
  });
}

export function useLeaveAbsenceTypeCatalog() {
  return useQuery({
    queryKey: leaveAbsenceTypeCatalogKey,
    queryFn: () => apiFetch('/leave/absence-types/catalog', leaveAbsenceTypeListSchema),
  });
}

export function useCreateLeaveAbsenceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaveAbsenceTypeRequest) =>
      apiFetch('/leave/absence-types', leaveAbsenceTypeSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave', 'absence-types'] });
    },
  });
}

export function useUpdateLeaveAbsenceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLeaveAbsenceTypeRequest }) =>
      apiFetch(`/leave/absence-types/${id}`, leaveAbsenceTypeSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave', 'absence-types'] });
    },
  });
}

export function useDeleteLeaveAbsenceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/leave/absence-types/${id}`, okResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave', 'absence-types'] });
    },
  });
}

export function useLeaveCapacityList() {
  return useQuery({
    queryKey: leaveCapacityKey,
    queryFn: () => apiFetch('/leave/capacity', leaveDepartmentCapacityListSchema),
  });
}

export function useUpsertLeaveCapacity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      departmentId,
      body,
    }: {
      departmentId: string;
      body: UpsertLeaveDepartmentCapacityRequest;
    }) =>
      apiFetch(`/leave/capacity/${departmentId}`, leaveDepartmentCapacityItemSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leaveCapacityKey });
    },
  });
}

export function useUpsertLeaveWorkPatternAdmin() {
  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: UpsertLeaveWorkPatternRequest }) =>
      apiFetch(`/leave/users/${userId}/pattern`, leaveWorkPatternSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  });
}

export function useCreateLeaveAdjustmentAdmin() {
  return useMutation({
    mutationFn: (body: CreateLeaveBalanceAdjustmentRequest) =>
      apiFetch('/leave/adjustments', leaveBalanceAdjustmentSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function usePreviewLeaveCompanyClosure() {
  return useMutation({
    mutationFn: (body: LeaveCompanyClosureWrite) =>
      apiFetch('/leave/company-closures/preview', leaveCompanyClosurePreviewSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useCreateLeaveCompanyClosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveCompanyClosureWrite) =>
      apiFetch('/leave/company-closures', leaveCompanyClosureResultSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useCreateLeaveOvertimeAdmin() {
  return useMutation({
    mutationFn: (body: CreateLeaveOvertimeRequest) =>
      apiFetch('/leave/overtime', leaveOvertimeEntrySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export type LeaveExportPageFilter = Pick<
  LeaveExportFilter,
  'year' | 'from' | 'to' | 'departmentId' | 'userId' | 'kind'
>;

function exportSearch(params: LeaveExportPageFilter & { format?: LeaveExportFormatCode }): string {
  const search = new URLSearchParams({
    year: String(params.year),
    kind: params.kind,
  });
  if (params.format) {
    search.set('format', params.format);
  }
  if (params.from) {
    search.set('from', params.from);
  }
  if (params.to) {
    search.set('to', params.to);
  }
  if (params.departmentId) {
    search.set('departmentId', params.departmentId);
  }
  if (params.userId) {
    search.set('userId', params.userId);
  }
  return search.toString();
}

export function useLeaveExportOptions() {
  return useQuery({
    queryKey: ['leave', 'export', 'options'] as const,
    queryFn: () => apiFetch('/leave/export/options', leaveExportOptionsSchema),
  });
}

export function useLeaveExportCount(filter: LeaveExportPageFilter, enabled: boolean) {
  return useQuery({
    queryKey: ['leave', 'export', 'count', filter] as const,
    enabled,
    queryFn: () => apiFetch(`/leave/export/count?${exportSearch(filter)}`, leaveExportCountSchema),
  });
}

export function useSetLeavePersonnelNumber() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, personnelNumber }: { userId: string } & SetPersonnelNumber) =>
      apiFetch(`/leave/users/${userId}/personnel-number`, setPersonnelNumberSchema, {
        method: 'PATCH',
        body: JSON.stringify({ personnelNumber }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave', 'people'] });
    },
  });
}

export async function downloadLeaveExport(
  params: LeaveExportPageFilter & { format: LeaveExportFormatCode },
): Promise<Pick<LeavePayrollExport, 'skipped'>> {
  const response = await fetch(`${API_BASE_URL}/leave/export?${exportSearch(params)}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(payload?.message ?? 'Der Export konnte nicht erstellt werden.');
  }
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    const body = leavePayrollExportSchema.parse(await response.json());
    const binary = Uint8Array.from(atob(body.contentBase64), (char) => char.charCodeAt(0));
    saveBlob(new Blob([binary], { type: body.mimeType }), body.filename);
    return { skipped: body.skipped };
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(disposition);
  const filename = decodeURIComponent(filenameMatch?.[1] ?? filenameMatch?.[2] ?? 'urlaub-export');
  saveBlob(blob, filename);
  return { skipped: [] };
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
